import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useAuth } from './useAuth';

type Role = 'subscriber' | 'creator' | 'admin' | 'moderator';
type CreatorStatus = 'none' | 'pending' | 'approved' | 'rejected' | 'suspended';

interface Options {
  creatorStatus?: CreatorStatus;
}

export function useRequireRole(
  required: Role[],
  options: Options = {},
): ReturnType<typeof useAuth> {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.session) {
      void navigate({ to: '/auth/sign-in' });
      return;
    }
    if (!auth.profile) return;

    if (auth.profile.suspended_at) {
      void navigate({ to: '/suspended' });
      return;
    }

    // Admin god-mode: an admin passes every role gate (e.g. the creator
    // workspace). This is UX access only — the underlying data stays RLS-scoped,
    // so admins see their own creator content, not everyone's. Moderators are
    // NOT included (Spec 21's scoped boundary).
    if (auth.profile.role === 'admin') return;

    const roleOk = required.includes(auth.profile.role as Role);
    const statusOk =
      !options.creatorStatus || auth.profile.creator_status === options.creatorStatus;

    if (!roleOk || !statusOk) {
      void navigate({ to: '/403' });
    }
  }, [auth.loading, auth.session, auth.profile, navigate, required, options.creatorStatus]);

  return auth;
}
